import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "task-step" }
const _hoisted_2 = { class: "task-step__title" }
const _hoisted_3 = { class: "task-step__form" }
const _hoisted_4 = ["src"]

import { ref, watch } from 'vue'
import { cloneProxy, getAxiosErrorText, idGenerator } from '@/common/helpers'
import { useScrollLock } from '@vueuse/core'

// Components
import TaskControls from '@/components/TaskControls'
import ProgressLine from '@/components/ProgressLine'
import InputFile from '@/components/inputs/InputFile'
import InputText from '@/components/inputs/InputText'
import InputDate from '@/components/inputs/InputDate'
import InputCard from '@/components/inputs/InputCard'
import { ElDialog, ElNotification } from 'element-plus'

// Router
import { useRouter, useRoute } from 'vue-router'
import type { Component } from 'vue'

// i18n
import { useI18n } from 'vue-i18n'
import { useTaskStore, useCommonStore } from '@/store'
import { i18n } from '@/locales/i18n'

export default /*#__PURE__*/_defineComponent({
  __name: 'TaskStep',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const id = route.params.id.toString()

// Types
const { t } = useI18n()

// Store
const taskStore = useTaskStore()
const commonStore = useCommonStore()

// Methods
function getComponent(name: string): Component | void {
  switch (name) {
    case 'file':
      return InputFile
    case 'string':
      return InputText
    case 'date':
      return InputDate
    case 'card':
      return InputCard
  }
}

function tryShowDemo(imgUrl: string): void {
  dialogImageUrl.value = imgUrl
  dialogVisible.value = true
}

// Data
const formData = ref<any>({})
const error = ref<any>({})
const dialogImageUrl = ref('')
const dialogVisible = ref(false)
const body = ref<HTMLElement | null>(document.body)
const isLocked = useScrollLock(body)

// Methods
function clear(): void {
  formData.value = {}
  error.value = {}
}

function validate(): Promise<boolean> {
  return new Promise((resolve, reject) => {
    error.value = {}
    let hasErrors = false

    taskStore.activeScreen?.poles.forEach(item => {

      if (item.type === 'file') {
        if (item.props.required && !item.props.value && !formData.value[item.id]) {
          hasErrors = true
          error.value[item.id] = t('validate.required')
        }
      }

      if (item.type === 'string') {
        if (item.props.required && !formData.value[item.id]) {
          hasErrors = true
          error.value[item.id] = t('validate.required')
        }

        if (item.props.required && formData.value[item.id].length < 19 && !error.value[item.id] && item.props?.mask === 'card') {
          hasErrors = true
          error.value[item.id] = t('validate.card')
        }
      }

      if (item.type === 'date') {
        if (item.props.required && !formData.value[item.id]) {
          hasErrors = true
          error.value[item.id] = t('validate.required')
        }
      }

      if (item.type === 'card') {
        const type = (typeof formData.value[item.id] === 'number') ? 'number' : 'string'
        if (item.props.required) {
          if (type === 'string' && formData.value[item.id].length > 0 && formData.value[item.id].length < 19) {
            hasErrors = true
            error.value[item.id] = t('validate.card')
          }
          if (type === 'string' && formData.value[item.id].length < 1) {
            hasErrors = true
            error.value[item.id] = t('validate.required')
          }
        } else {
          if (type === 'string' && formData.value[item.id].length > 0 && formData.value[item.id].length < 19) {
            hasErrors = true
            error.value[item.id] = t('validate.required')
          }
        }
      }
    })

    if (hasErrors) {
      reject()
    } else {
      resolve(true)
    }
  })
}

async function next(): Promise<void> {
  try {
    await validate()
  } catch (e) {
    return
  }
  try {
    commonStore.loading = true
    const data = {
      id_task: id,
      id_step: taskStore.activeScreen?.id_step,
      poles: cloneProxy(formData.value),
    }
    await taskStore.saveStep(data)
    commonStore.user = await commonStore.getUser()

    if (taskStore.isLastScreen) {
      await router.push({ name: 'Main' })
    } else {
      taskStore.task = await taskStore.getTask(id)
    }
    clear()
  } catch (e: any) {
    ElNotification({
      title: i18n.t('common.error'),
      message: getAxiosErrorText(e),
      type: 'error',
    })
  } finally {
    commonStore.loading = false
  }
}

function prev(): void {
  clear()
  taskStore.prevStep()
}

// Watch
watch(dialogVisible, (val) => {
  isLocked.value = val
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(ProgressLine), { class: "task-step__progress-line" }),
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_unref(taskStore).activeScreen.name), 1),
    _createElementVNode("div", _hoisted_3, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(taskStore).activeScreen.poles, (item, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: `file-input-${item.id}-${index}`,
          class: "task-step__form-item"
        }, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(getComponent(item.type)), _mergeProps({
            key: `component-${item.id}-${index}`
          }, item.props, {
            theme: "inTask",
            modelValue: formData.value[item.id],
            "onUpdate:modelValue": ($event: any) => ((formData.value[item.id]) = $event),
            error: error.value[item.id],
            "onUpdate:error": ($event: any) => ((error.value[item.id]) = $event),
            onShowDemo: tryShowDemo
          }), null, 16, ["modelValue", "onUpdate:modelValue", "error", "onUpdate:error"])),
          (!getComponent(item.type))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode(_toDisplayString(item), 1)
              ], 64))
            : _createCommentVNode("", true)
        ]))
      }), 128))
    ]),
    _createVNode(_unref(ElDialog), {
      "lock-scroll": false,
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dialogVisible).value = $event))
    }, {
      default: _withCtx(() => [
        _createElementVNode("img", {
          style: {"width":"100%"},
          src: dialogImageUrl.value
        }, null, 8, _hoisted_4)
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_unref(TaskControls), {
      onNextStep: next,
      onPrevStep: prev
    })
  ]))
}
}

})